export type Volume = {
  numeral: string;
  title: string;
  tagline: string;
  intro: string[];
  listLabel: string;
  items: string[];
  goal: string;
  note?: string;
};

export const volumes: Volume[] = [
  {
    numeral: "I",
    title: "The Facelift Case Assessment Framework",
    tagline: "Organize the case before studying the procedure.",
    intro: [
      "I created this volume to help you organize the educational review of a facelift case before moving into more detailed procedural concepts.",
      "Instead of approaching the case as a collection of unrelated observations, you'll use a consistent framework to organize the information available.",
    ],
    listLabel: "Inside this volume, you'll find",
    items: [
      "Patient profile and relevant history",
      "Aesthetic objectives and expectations",
      "Facial anatomy review prompts",
      "Skin and tissue characteristics",
      "Facial aging observations",
      "Asymmetry documentation prompts",
      "Previous procedures and relevant background",
      "Patient goals and educational considerations",
      "Questions requiring further investigation",
      "Case documentation templates",
      "Preoperative review worksheets",
      "A structured case-summary format",
    ],
    goal: "Turn a collection of observations into an organized educational case summary.",
  },
  {
    numeral: "II",
    title: "The Facelift Planning & Anatomy Map",
    tagline: "Organize the variables that shape your understanding of a case.",
    intro: [
      "Facelift education involves multiple anatomical and aesthetic considerations.",
      "This volume provides a visual framework for organizing the concepts you want to review and the relationships between them.",
    ],
    listLabel: "You'll explore",
    items: [
      "Facial anatomy review categories",
      "Facial aging observations",
      "Skin and tissue characteristics",
      "Facial proportions and asymmetry",
      "Aesthetic objectives",
      "Patient-specific considerations",
      "Questions related to facial anatomy",
      "Reference-review prompts",
      "Case comparison worksheets",
      "A visual planning map",
      "An information-gap checklist",
      "A structured framework for documenting your observations",
    ],
    goal: "A clearer way to organize your preoperative learning.",
    note: "The purpose isn't to generate automatic clinical decisions. It's to help you organize the information and identify the questions that require closer professional review.",
  },
  {
    numeral: "III",
    title: "The Facelift Procedure Blueprint",
    tagline: "Organize your understanding of the complete educational procedure sequence.",
    intro: [
      "This is the core of the system.",
      "Rather than presenting isolated concepts, I designed this volume to help you connect the different stages of facelift education within a coherent review framework.",
    ],
    listLabel: "You'll review",
    items: [
      "Preoperative preparation concepts",
      "Relevant anatomical considerations",
      "Educational overviews of facelift approaches",
      "Procedure-sequence review categories",
      "Technical concepts requiring further study",
      "Intraoperative considerations as presented in verified educational references",
      "Questions for reviewing symmetry and aesthetic objectives",
      "Closure and finishing concepts",
      "Immediate postoperative review considerations",
      "A procedure-review worksheet",
      "A framework for comparing educational references",
      "A structured record of concepts reviewed and questions remaining",
    ],
    goal: "Organize your understanding of the subject rather than reconstructing the entire learning sequence from scattered resources.",
    note: "This is an educational review framework, not a universal operative protocol or a substitute for supervised surgical training.",
  },
  {
    numeral: "IV",
    title: "The Facelift Case Atlas",
    tagline: "Practice your educational reasoning instead of simply reading theory.",
    intro: [
      "This is where the Blueprint becomes an active learning experience.",
      "You'll receive structured educational case exercises designed to help you apply the framework consistently.",
    ],
    listLabel: "For each educational case, you'll",
    items: [
      "Review the available case information",
      "Identify relevant observations",
      "Organize the questions that deserve attention",
      "Document the information that remains unclear",
      "Consult the relevant educational references",
      "Compare your observations with the educational discussion",
      "Record the concepts you want to revisit",
    ],
    goal: "Instead of simply consuming another lecture, you'll actively practice organizing what you learn.",
    note: "The case exercises are designed to encourage structured reflection rather than provide definitive patient-specific surgical decisions.",
  },
  {
    numeral: "V",
    title: "The Facelift Complication & Case Review Atlas",
    tagline: "Organize your study of challenging scenarios.",
    intro: [
      "Complex educational scenarios can be difficult to review when the information is scattered across multiple sources.",
      "I designed this volume to give you a consistent framework for studying complications and challenging case discussions.",
    ],
    listLabel: "For each educational scenario, you'll explore",
    items: [
      "What happened in the scenario?",
      "What information is available?",
      "Which questions deserve further investigation?",
      "What relevant factors should be reviewed?",
      "Which professional references may help explain the situation?",
      "What information should be documented?",
      "Which concepts remain uncertain?",
      "What educational lessons can be extracted?",
      "Which topics deserve additional study?",
    ],
    goal: "Not to memorize a list of complications — but to develop a more organized approach to reviewing difficult scenarios.",
    note: "Clinical interpretation and management decisions must always rely on appropriate professional training, current evidence, and individualized clinical judgment.",
  },
  {
    numeral: "VI",
    title: "The Surgeon's Facelift Learning Archive",
    tagline: "Turn every educational case into a documented learning opportunity.",
    intro: [
      "One of the most useful habits in professional education is keeping a consistent record of what you've studied.",
      "This volume gives you the templates to build your own facelift learning archive.",
    ],
    listLabel: "You'll receive templates for",
    items: [
      "Case documentation",
      "Preoperative educational review",
      "Procedure-concept review",
      "Case reflection",
      "Comparison between educational cases",
      "Learning-point summaries",
      "Questions for future review",
      "Knowledge-gap tracking",
      "Monthly study reviews",
      "Personal learning goals",
      "Reference tracking",
      "Annual educational reflection",
    ],
    goal: "Every case becomes another opportunity to organize your learning.",
  },
];

export const tools = [
  { title: "The 15-Minute Facelift Review Sheet", body: "A structured worksheet designed to help you organize an educational case review in approximately 15 minutes." },
  { title: "The Preoperative Review Checklist", body: "A repeatable checklist for organizing the information you want to review before studying a case." },
  { title: "The Facelift Anatomy & Planning Map", body: "A visual framework for organizing anatomical observations, aesthetic objectives, and questions for further study." },
  { title: "The Case Comparison Matrix", body: "Compare educational cases side by side and document their similarities, differences, and learning points." },
  { title: "The Visual Case Documentation Guide", body: "An educational guide for organizing clinical photography and visual case documentation while respecting patient consent, confidentiality, and applicable professional requirements." },
];
